import { createContext, useContext, useEffect, useCallback, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const NavigationContext = createContext(null);

/**
 * Route definitions for deep linking
 */
export const ROUTES = {
    HOME: '/',
    DASHBOARD: '/sanctuary',
    TAROT: '/tarot',
    ORBIT: '/orbit',
    DREAMS: '/dreams',
    SETTINGS: '/settings',
    PROFILE: '/profile'
};

/**
 * NavigationProvider - Handles browser/Android back button navigation with deep linking
 * 
 * This provides:
 * 1. Push new entries to browser history for major sections
 * 2. System back button navigates to previous section
 * 3. No UI back button required - uses native gestures/buttons
 */
export const NavigationProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [canGoBack, setCanGoBack] = useState(false);
    const isNavigatingRef = useRef(false);
    const historyStackRef = useRef([]);

    // Track history stack internally
    useEffect(() => {
        const currentPath = location.pathname + location.search + location.hash;
        
        // Avoid duplicate entries
        if (historyStackRef.current[historyStackRef.current.length - 1] !== currentPath) {
            historyStackRef.current.push(currentPath);
            // Keep only last 50 entries
            if (historyStackRef.current.length > 50) {
                historyStackRef.current.shift();
            }
        }
        
        setCanGoBack(historyStackRef.current.length > 1);
    }, [location]);

    // Handle browser back button (popstate event)
    useEffect(() => {
        const handlePopState = (event) => {
            // Mark that we're handling a back navigation
            isNavigatingRef.current = true;
            
            // Update our internal stack
            if (historyStackRef.current.length > 1) {
                historyStackRef.current.pop();
            }
            
            // The browser has already navigated, just update our state
            setCanGoBack(historyStackRef.current.length > 1);
            
            // Reset navigation flag
            setTimeout(() => {
                isNavigatingRef.current = false;
            }, 100);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Push a new route to history stack (for major sections)
    const pushRoute = useCallback((route, options = {}) => {
        // Don't push if we're already on this route
        if (location.pathname === route) return;
        
        // Don't push during back navigation
        if (isNavigatingRef.current) return;
        
        // Use replace for modals/overlays, push for major sections
        if (options.replace) {
            navigate(route, { replace: true });
        } else {
            navigate(route);
        }
    }, [navigate, location.pathname]);

    // Go back in history
    const goBack = useCallback(() => {
        if (historyStackRef.current.length > 1) {
            navigate(-1);
            return true;
        }
        return false;
    }, [navigate]);

    // Register a custom back handler (for modals, drawers, etc.)
    const [customBackHandlers, setCustomBackHandlers] = useState([]);

    const registerBackHandler = useCallback((handler, priority = 0) => {
        const handlerId = Date.now() + Math.random();
        setCustomBackHandlers(prev => [...prev, { id: handlerId, handler, priority }].sort((a, b) => b.priority - a.priority));
        return () => setCustomBackHandlers(prev => prev.filter(h => h.id !== handlerId));
    }, []);

    // Execute back action (checks custom handlers first)
    const handleBack = useCallback(() => {
        // Check custom handlers in priority order (modals, drawers, etc.)
        for (const { handler } of customBackHandlers) {
            const handled = handler();
            if (handled) return true;
        }
        
        // Otherwise, try to go back in history
        return goBack();
    }, [customBackHandlers, goBack]);

    // Get current section from path
    const getCurrentSection = useCallback(() => {
        const path = location.pathname;
        if (path.startsWith('/tarot')) return 'tarot';
        if (path.startsWith('/orbit')) return 'orbit';
        if (path.startsWith('/dreams')) return 'dreams';
        if (path.startsWith('/settings')) return 'settings';
        if (path.startsWith('/profile')) return 'profile';
        if (path.startsWith('/sanctuary') || path === '/') return 'dashboard';
        return 'dashboard';
    }, [location.pathname]);

    return (
        <NavigationContext.Provider value={{
            canGoBack,
            goBack,
            handleBack,
            pushRoute,
            registerBackHandler,
            getCurrentSection,
            currentPath: location.pathname,
            ROUTES
        }}>
            {children}
        </NavigationContext.Provider>
    );
};

/**
 * useNavigation - Hook to access navigation context
 */
export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        // Return a mock context for components outside NavigationProvider
        return {
            canGoBack: false,
            goBack: () => false,
            handleBack: () => false,
            pushRoute: () => {},
            registerBackHandler: () => () => {},
            getCurrentSection: () => 'dashboard',
            currentPath: '/',
            ROUTES
        };
    }
    return context;
};

/**
 * useBackHandler - Hook to register a custom back handler
 * 
 * Usage in a component:
 * useBackHandler(() => {
 *   if (modalOpen) {
 *     closeModal();
 *     return true; // Handled
 *   }
 *   return false; // Not handled, continue to default
 * });
 */
export const useBackHandler = (handler, priority = 0) => {
    const { registerBackHandler } = useNavigation();
    
    useEffect(() => {
        if (handler) {
            return registerBackHandler(handler, priority);
        }
    }, [handler, registerBackHandler, priority]);
};

export default NavigationContext;
